import React, {
  Fragment, ReactElement, useEffect, useRef, useState,
} from 'react'
import Modal from 'react-bootstrap/Modal'
import Container from 'react-bootstrap/Container'
import Button from '../Button'
import { useReactToPrint } from 'react-to-print'
import * as S from './TagPrinterManagerPerBlock.styles'
import { DarkGreen, LigthGreen } from 'src/assets/colors'

interface TagPrinterManagerPerBlockProps {
  showTagModal: boolean
  setShowTagModal: React.Dispatch<React.SetStateAction<boolean>>
  setClickPrint?: React.Dispatch<React.SetStateAction<boolean>>
  actionAfterClose?: () => void
  modalTitle: string
  tagsToPrint: ReactElement[]
  printLimit: number
}

interface BlockItem {
  id: number
  printed: boolean
  block: React.ReactElement[]
}

function TagPrinterManagerPerBlock({
  showTagModal,
  setShowTagModal,
  setClickPrint,
  actionAfterClose,
  modalTitle,
  tagsToPrint = [],
  printLimit,
}: TagPrinterManagerPerBlockProps) {
  const [blocks, setBlocks] = useState<BlockItem[]>([])
  const [batchBlocksToPrint, setBatchBlocksToPrint] = useState<React.ReactElement[]>([])
  const [sendToPrint, setSendToPrint] = useState(false)

  const handleCloseModal = () => {
    setShowTagModal(false)

    if (actionAfterClose) actionAfterClose()

    if (setClickPrint) {
      setClickPrint(true)
    }
  }

  useEffect(() => {
    const divideArrayIntoBlocks = (arr: React.ReactElement[], blockSize: number) => {
      const dividedArray: BlockItem[] = []
      let index = 0
      let idCounter = 1
      while (index < arr.length) {
        dividedArray.push({
          id: idCounter,
          printed: false,
          block: arr.slice(index, index + blockSize),
        })
        index += blockSize
        idCounter += 1
      }
      return dividedArray
    }

    const dividedBlocks = divideArrayIntoBlocks(tagsToPrint, printLimit)

    setBlocks(dividedBlocks)
  }, [printLimit, tagsToPrint])

  const handleBlockPrint = (index: number) => {
    const selectedBlock = blocks[index]
    setBatchBlocksToPrint(selectedBlock.block)
    setSendToPrint(true)

    const updatedBlocks = [...blocks]
    updatedBlocks[index] = {
      ...selectedBlock,
      printed: true,
    }

    setTimeout(() => {
      setBlocks(updatedBlocks)
    }, 1000)
  }

  const componentRef = useRef(null)

  const print = useReactToPrint({
    content: () => componentRef.current,
    copyStyles: true,
    onAfterPrint: () => setBatchBlocksToPrint([]),
  })

  useEffect(() => {
    if (sendToPrint && batchBlocksToPrint.length > 0) {
      print()
      setSendToPrint(false)
    }
  }, [batchBlocksToPrint, print, sendToPrint])

  return (
    <>
      <Modal
        show={showTagModal}
        onHide={handleCloseModal}
        backdrop='static'
        keyboard={false}
        size='lg'
        style={{ height: '100vh', margin: '0 auto' }}
        centered
        data-testid='tag-printer-modal-per-block-test-id'
      >
        <Modal.Header style={{ padding: '0 1rem' }}>
          <Modal.Title>{modalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '0' }}>
          <Container fluid='md'>
            {blocks.map((item, index) => (
              <S.Row key={item.id}>
                <S.Col>{`# ${String(index + 1).padStart(3, '0')}`}</S.Col>
                <S.ColQty>
                  {`${item.block.length < 10 ? '0' : ''}${item.block.length} 
                          ${item.block.length > 1 ? 'etiquetas' : 'etiqueta'}`}
                </S.ColQty>
                <S.ColBtn>
                  <Button
                    text={
                        item.printed
                          ? 'receivement.labelPrinting.reprintButton'
                          : 'receivement.labelPrinting.printButton'
                      }
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '.2rem',
                      width: '6rem',
                    }}
                    onClick={() => handleBlockPrint(index)}
                    testId='button-print-block'
                    color={DarkGreen}
                    hoverColor={LigthGreen}
                  />
                </S.ColBtn>
                <S.ColIcon>
                  {
                    item.printed && <S.StyledDoneIcon />
                  }
                </S.ColIcon>
              </S.Row>
            ))}
          </Container>
        </Modal.Body>

        <Button
          text='receivement.labelPrinting.doneButton'
          style={{ margin: '.2rem' }}
          isDisabled={blocks.some((block) => !block.printed)}
          onClick={() => handleCloseModal()}
          testId='button-done-print-block'
        />
      </Modal>

      {batchBlocksToPrint.length > 0 && (
        <div style={{ display: 'none' }}>
          <div ref={componentRef} style={{ margin: '40px' }}>
            {batchBlocksToPrint?.map((tag, index) => (
              <Fragment key={index}>
                <S.PageBreak />
                {tag}
              </Fragment>
            ))}
          </div>
        </div>
      )}
    </>
  )
}

export default TagPrinterManagerPerBlock
